<!-- =========================================================================================
  File Name: UserList.vue
  Description: User List page
  ----------------------------------------------------------------------------------------
  Item Name: Vuexy - Vuejs, HTML & Laravel Admin Dashboard Template
  Author: Pixinvent
  Author URL: http://www.themeforest.net/user/pixinvent
========================================================================================== -->

<template>
	<div id="page-user-list">
		<slot></slot>
		<div :class="customConfig.showPopup ? 'p-6' : 'vx-card p-6'">
			<div class="flex flex-wrap items-center justify-end" v-if="customConfig.tableOptions">
				<!-- ITEMS PER PAGE -->
				<div v-if="customConfig.showItemsPerPage" class="flex-grow">
					<vs-dropdown vs-trigger-click class="cursor-pointer">
						<div
							class="p-4 border border-solid d-theme-border-grey-light rounded-full d-theme-dark-bg cursor-pointer flex items-center justify-between font-medium"
						>
							<span class="mr-2"
								>{{
									paginationPageSize === 0
										? 0
										: currentPage * paginationPageSize - (paginationPageSize - 1)
								}}
								-
								{{
									usersData.length - currentPage * paginationPageSize > 0
										? currentPage * paginationPageSize
										: usersData.length
								}}
								{{ $t("Of") }} {{ pagination.total_records }}</span
							>
							<feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
						</div>
						<vs-dropdown-menu>
							<vs-dropdown-item @click="paginationSetPageSize(20)">
								<span>20</span>
							</vs-dropdown-item>
							<vs-dropdown-item @click="paginationSetPageSize(50)">
								<span>50</span>
							</vs-dropdown-item>
							<vs-dropdown-item @click="paginationSetPageSize(100)">
								<span>100</span>
							</vs-dropdown-item>
							<vs-dropdown-item @click="paginationSetPageSize(200)">
								<span>200</span>
							</vs-dropdown-item>
						</vs-dropdown-menu>
					</vs-dropdown>
				</div>
				<template>
					<!-- ITEMS PER PAGE -->
					<vs-dropdown vs-trigger-click class="cursor-pointer">
						<div
							class="p-3 mt-1 mr-2 border border-solid d-theme-border-grey-light rounded-lg d-theme-dark-bg cursor-pointer font-medium"
						>
							<span class="mr-2">{{ $t("SearchBy") }} {{ searchSelected }}</span>
							<feather-icon icon="ChevronDownIcon" svgClasses="h-4 w-4" />
						</div>
						<vs-dropdown-menu>
							<vs-dropdown-item @click="setSearchField('Identification')">
								<span>{{ $t("ResourceModule.Table.Identification") }}</span>
							</vs-dropdown-item>
							<vs-dropdown-item @click="setSearchField('Address')">
								<span>{{ $t("ResourceModule.Table.Address") }}</span>
							</vs-dropdown-item>
							<vs-dropdown-item @click="setSearchField('Department')">
								<span>{{ $t("ResourceModule.Table.Department") }}</span>
							</vs-dropdown-item>
							<vs-dropdown-item @click="setSearchField('City')">
								<span>{{ $t("ResourceModule.Table.City") }}</span>
							</vs-dropdown-item>
						</vs-dropdown-menu>
					</vs-dropdown>
				</template>
				<!-- TABLE ACTION COL-2: SEARCH & EXPORT AS CSV -->
				<div class="vx-col md:w-1/3 sm:w-1/2 w-full p-0">
					<vx-tooltip
						class="cursor-pointer"
						color="primary"
						position="bottom"
						delay=".1s"
						:title="$t('Search')"
						:text="$t('SearchSeparatedByCommas') + '.'"
					>
						<tags-input
							element-id="tags"
							v-model="searchQuery"
							add-tags-on-comma
							add-tags-on-blur
							:placeholder="$t('Teams.Form.Add') + ' ' + searchSelected"
							:typeahead="true"
							@keyup.enter="searchVisitPoint"
						></tags-input>
					</vx-tooltip>
				</div>
				<vs-button
					class="ml-2"
					color="primary"
					icon-pack="feather"
					icon="icon-search"
					:title="$t('VisitPoints.Search')"
					@click.prevent="searchVisitPoint"
				></vs-button>

				<vs-button
					class="ml-2"
					color="primary"
					icon-pack="feather"
					icon="icon-refresh-cw"
					:title="$t('Refresh')"
					@click.prevent="refreshVisitPoint()"
				></vs-button>
				<vs-dropdown v-if="customConfig.showActions" vs-trigger-click class="cursor-pointer">
					<vs-button class="flex items-center justify-between" type="filled">
						<span class="mr-2">{{ $t("BreakTable.Action") }}</span>
						<vs-icon icon="expand_more"></vs-icon>
					</vs-button>
					<vs-dropdown-menu style="min-width: 15rem">
						<vs-dropdown-item @click="$router.push('/apps/visit-point/create')">
							<span class="flex items-center">
								<feather-icon icon="PlusSquareIcon" svgClasses="h-4 w-4" class="mr-2" />
								<span>{{ $t("Add") }} {{ $t("VisitPoint") }}</span>
							</span>
						</vs-dropdown-item>
						<vs-dropdown-item @click.prevent="selectedVisitsPoint">
							<span class="flex items-center">
								<feather-icon icon="CheckSquareIcon" svgClasses="h-4 w-4" class="mr-2" />
								<span>{{ $t("ShowSelected") | capitalize }}</span>
							</span>
						</vs-dropdown-item>
					</vs-dropdown-menu>
				</vs-dropdown>
				<vs-button
					v-else-if="!customConfig.showActions"
					class="ml-2"
					color="primary"
					icon-pack="feather"
					icon="icon-plus"
					:title="$t('Add') + ' ' + $t('VisitPoint')"
					@click.prevent="$router.push('/apps/visit-point/create')"
				></vs-button>
				<vs-dropdown
					vs-custom-content
					:vs-trigger-click="true"
					class="cursor-pointer"
					:title="$t('Setting.Setting')"
				>
					<vs-button class="ml-2" type="filled" icon-pack="feather" icon="icon-settings"></vs-button>
					<vs-dropdown-menu style="z-index: 54000; width: 30%">
						<vx-card no-shadow :title="$t('Setting.Setting')">
							<div class="flex flex-row justify-between p-1">
								<p class="mr-2">{{ $t("Preferences.FindExactly") }}</p>
								<vs-switch color="success" v-model="isExactly" @input="openEnableConfirm"></vs-switch>
							</div>
						</vx-card>
					</vs-dropdown-menu>
				</vs-dropdown>
			</div>
			<!-- AgGrid Table -->
			<ag-grid-vue
				ref="agGridTable"
				class="ag-theme-material w-100 my-4 ag-grid-table"
				:class="[{'ag-customizer-container': !isWidthPortatil}]"
				colResizeDefault="shift"
				:components="components"
				:gridOptions="gridOptions"
				:style="!customConfig.tableOptions ? 'padding-top: 80px' : ''"
				:columnDefs="columnDefs"
				:defaultColDef="defaultColDef"
				:rowData="usersData"
				:rowSelection="customConfig.rowSelection"
				:animateRows="true"
				:pagination="true"
				:paginationPageSize="paginationPageSize"
				:suppressPaginationPanel="true"
				:enableRtl="$vs.rtl"
				:overlayNoRowsTemplate="overlayNoRowsTemplate"
				:overlayLoadingTemplate="overlayLoadingTemplate"
				:enableCellTextSelection="true"
				@grid-ready="onGridReady"
				@rowSelected="rowSelected"
				@selection-changed="onSelectionChanged"
			>
			</ag-grid-vue>
			<vs-pagination :total="totalPages" :max="7" v-model="currentPage" />
		</div>
	</div>
</template>

<script>
import {visitPoinListMixin} from "../model/VisitPointListMixin.js";

export default {
	mixins: [visitPoinListMixin],
};
</script>

<style lang="scss" scoped>
#page-user-list {
	.user-list-filters {
		.vs__actions {
			position: absolute;
			right: 0;
			top: 50%;
			transform: translateY(-58%);
		}
	}
}
</style>
